import type { FirebaseService } from './FirebaseService';
import {
	createServerRenderingAuthService,
	SignInState,
	type AuthResultBetterAuth,
	type AuthResultJwt,
	type AuthService,
	type User
} from './AuthService.svelte';
import { fetchRetry } from './utils';
import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
import z from 'zod';
import { authClient } from './auth-client';
import _ from 'lodash';

export function createBetterAuthAuthService(firebaseService: FirebaseService): AuthService {
	if (typeof window === 'undefined' || firebaseService == undefined) {
		return createServerRenderingAuthService();
	}
	return createAuthServiceImpl(firebaseService);
}

function createAuthServiceImpl(firebaseService: FirebaseService): AuthService {
	let currentUser = $state<User | null>();
	let signInState = $state<SignInState>(SignInState.SignedOut);
	let isAuthReady = false;

	const createUser = (user: { id: string; email: string | null; name: string | null }) => {
		return {
			displayName: user.name,
			email: user.email,
			uid: user.id,
			getIdToken: async () => {
				throw new Error('not supported');
			}
		} satisfies User;
	};

	const onAuthStateChanged = (callback: (currentUser: User | null) => void) => {
		return authClient.useSession().subscribe((data) => {
			const currentUser = data.data && createUser(data.data.user);
			callback(currentUser);
		});
	};

	const authReady = async () => {
		const promise = new Promise<void>((resolve) => {
			if (isAuthReady) {
				resolve();
				return;
			}
			const session = authClient.useSession();
			if (!session.get().isPending) {
				resolve();
				return;
			}
			const unsub = session.listen((val) => {
				if (val.isPending == false) {
					// Session is available
					isAuthReady = true;
					unsub();
					resolve();
				}
			});
		});
		return promise;
	};

	type AuthStateChangedCallback = (currentUser: User | null) => void;
	const authStateChangedCallbacks: Array<AuthStateChangedCallback> = [];
	function addAuthStateChangedCallback(callback: AuthStateChangedCallback) {
		const wrapped_callback = (x: User | null) => callback(x);
		if (currentUser !== undefined) {
			wrapped_callback(currentUser);
		}
		authStateChangedCallbacks.push(wrapped_callback);
		return () => {
			_.remove(authStateChangedCallbacks, (value) => value === wrapped_callback);
		};
	}
	function invokeAuthStateChangedCallbacks() {
		if (currentUser === undefined) {
			return;
		}
		for (const callback of authStateChangedCallbacks) {
			callback(currentUser);
		}
	}

	function authStateChanged(user: User | null) {
		currentUser = user;
		if (!user) {
			signInState = SignInState.SignedOut;
		} else {
			signInState = SignInState.SignedIn;
		}
		invokeAuthStateChangedCallbacks();
	}

	onAuthStateChanged(authStateChanged);

	// Preload firebase auth once the component has been loaded initially
	let firebaseAuthReady = $state(false);
	const firebaseAuth = (async () => {
		const auth = await firebaseService.getAuth();
		const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
		await auth.authStateReady();
		firebaseAuthReady = true;
		return { auth, GoogleAuthProvider, signInWithPopup };
	})();

	return {
		getSignInState: () => signInState,
		async signOut() {
			await authClient.signOut();
			authStateChanged(null);
		},
		authReady,
		onAuthStateChanged: addAuthStateChangedCallback,
		getUser() {
			return currentUser ?? null;
		},
		firebaseAuthReady() {
			return firebaseAuthReady;
		},
		async signInWithGoogleIdToken(_idToken: string) {
			throw new Error('not supported');
		},
		async signInWithGoogle() {
			const { auth, GoogleAuthProvider, signInWithPopup } = await firebaseAuth;
			const provider = new GoogleAuthProvider();
			const credentials = await signInWithPopup(auth, provider);

			const idToken = await credentials.user.getIdToken();

			const oauthCredentials = GoogleAuthProvider.credentialFromResult(credentials);
			const googleIdToken = oauthCredentials?.idToken;

			if (!idToken) {
				throw new Error('Id token unavailable');
			}

			if (!googleIdToken) {
				throw new Error('Google Id token unavailable');
			}

			const result = await authClient.firebaseIntegration.signInWithGoogle({ idToken });
			if (!result.data) {
				throw new Error('Session creation failed');
			}
			const user = createUser(result.data.user);
			authStateChanged(user);

			return {
				user,
				provider: 'firebase',
				method: 'better-auth',
				idToken: googleIdToken,
				sessionToken: result.data.session.token
			} satisfies AuthResultBetterAuth;
		},
		async requestEmailOtp(email) {
			await authReady();
			await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
		},
		async signInWithEmailOtp(email, otp) {
			await authReady();
			const result = await authClient.signIn.emailOtp({ email, otp });
			if (!result.data?.user) {
				throw new Error('Loging using email otp failed');
			}

			const user = createUser(result.data?.user);
			authStateChanged(user);

			return {
				user,
				provider: 'email-otp',
				method: 'better-auth',
				sessionToken: result.data.token
			} satisfies AuthResultBetterAuth;
		},
		async signInWithJwtTokens() {
			throw new Error('not supported');
		},
		async upgradeToJwt() {
			if (!currentUser) {
				throw new Error('User must be signed in');
			}
			const resp = await fetchRetry(
				`${PUBLIC_VOICE_ALIGN_WEBSITE}/api/activation/create-login-token`,
				{
					method: 'POST'
				}
			);
			if (!resp.ok) {
				throw new Error('creating login token was not allowed');
			}
			const result = z.object({ idToken: z.jwt(), refreshToken: z.jwt() }).parse(await resp.json());
			return {
				idToken: result.idToken,
				refreshToken: result.refreshToken,
				method: 'jwt',
				provider: 'api',
				user: currentUser
			} satisfies AuthResultJwt;
		}
	};
}
